import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Chip, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Tooltip, alpha, useTheme, Divider, Skeleton,
  Collapse, TextField, FormControl, InputLabel, Select, SelectChangeEvent,
  Popover, Badge,
} from '@mui/material';
import {
  Add, MoreVert, Download, Payments, MoneyOff, CheckCircle, Cancel, Delete,
  Mail as MailIcon, ExpandMore, ExpandLess, FilterList, Refresh,
  ClearAll,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import letterService from '../../services/letterService';
import { useLetterPdf } from './useLetterPdf';
import BusyIcon from '../common/BusyIcon';
import finePaymentService from '../../services/finePaymentService';
import {
  LetterV2, LetterStatus, LetterPaymentStatus, StudentLettersSummary,
  FinePayment,
} from '../../types/letters';
import { severityChipSx, severityTone, paymentStatusSx } from '../../utils/severityTone';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import IssueLetterDialog from './IssueLetterDialog';
import RecordPaymentDialog from '../fines/RecordPaymentDialog';
import WaiveFineDialog from '../fines/WaiveFineDialog';
import ConfirmationDialog from '../common/ConfirmationDialog';
import DateRangePicker from '../common/DateRangePicker';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface Props {
  studentId: string;
  studentName?: string;
  /** Called after a letter is issued, paid, waived, resolved, cancelled or deleted. */
  onChanged?: () => void;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const paymentPillLabel = (l: LetterV2, formatCurrency: (n: number) => string): string => {
  if (!l.amount) return '';
  switch (l.paymentStatus) {
    case 'paid':    return `Paid`;
    case 'waived':  return `Waived`;
    case 'partial': return `${formatCurrency(l.paidAmount || 0)} / ${formatCurrency(l.amount || 0)}`;
    case 'unpaid':
    default:        return `Unpaid · ${formatCurrency(l.amount || 0)}`;
  }
};

const StatusBadge: React.FC<{ status: LetterStatus }> = ({ status }) => {
  const theme = useTheme();
  if (status === 'active') return null;
  const map = {
    resolved: { label: 'Resolved', color: theme.palette.success.main },
    cancelled: { label: 'Cancelled', color: theme.palette.text.disabled },
    active: { label: 'Active', color: theme.palette.info.main },
  } as const;
  const v = map[status];
  return (
    <Chip
      label={v.label}
      size="small"
      sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(v.color, 0.1), color: v.color, fontWeight: 600 }}
    />
  );
};

const LetterTimelineItem: React.FC<{
  letter: LetterV2;
  onAction: (kind: 'download' | 'pay' | 'waive' | 'resolve' | 'cancel' | 'delete', l: LetterV2) => void | Promise<void>;
  canWriteLetters: boolean;
  canWriteFines: boolean;
  /** Its PDF is being prepared — the menu stays open with a spinner until it's ready. */
  downloading: boolean;
}> = ({ letter, onAction, canWriteLetters, canWriteFines, downloading }) => {
  const theme = useTheme();
  const formatCurrency = useCurrencyFormat();
  const tone = severityTone(theme, letter.categoryId?.severity);
  const [expanded, setExpanded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [payments, setPayments] = useState<FinePayment[] | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const isCancelled = letter.status === 'cancelled';
  const canPay = canWriteFines && letter.amount && letter.status === 'active' &&
    (letter.paymentStatus === 'unpaid' || letter.paymentStatus === 'partial');
  const canWaive = canPay;
  const canResolve = canWriteLetters && letter.status === 'active';
  const canCancel = canWriteLetters && letter.status !== 'cancelled';

  const togglePayments = async () => {
    if (!letter.amount) return;
    const next = !expanded;
    setExpanded(next);
    if (next && payments === null) {
      setLoadingPayments(true);
      try {
        const list = await finePaymentService.listLetterPayments(letter._id);
        setPayments(list);
      } catch {
        setPayments([]);
      } finally {
        setLoadingPayments(false);
      }
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        pl: 1.75,
        py: 1.25,
        borderBottom: `1px solid ${theme.palette.divider}`,
        opacity: isCancelled ? 0.6 : 1,
        '&:last-of-type': { borderBottom: 'none' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, top: 14, bottom: 14,
          width: 2, borderRadius: 1,
          bgcolor: tone.main,
        },
        '&:hover': { bgcolor: alpha(tone.main, 0.025) },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Line 1 — category + title (one line, truncated). */}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            {letter.categoryId && (
              <Chip
                label={letter.categoryId.label}
                size="small"
                sx={{ ...severityChipSx(theme, letter.categoryId.severity), height: 20, fontSize: '0.68rem', flexShrink: 0 }}
              />
            )}
            <StatusBadge status={letter.status} />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
              title={letter.title || letter.reason || ''}
            >
              {letter.title || letter.reason || '—'}
            </Typography>
          </Stack>

          {/* Line 2 — date · by · payment pill (when applicable) */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(letter.issuedAt)}
            </Typography>
            {letter.issuedBy?.name && (
              <>
                <Box sx={{ width: 3, height: 3, bgcolor: 'text.disabled', borderRadius: '50%' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {letter.issuedBy.name}
                </Typography>
              </>
            )}
            {letter.amount != null && (
              <>
                <Box sx={{ width: 3, height: 3, bgcolor: 'text.disabled', borderRadius: '50%' }} />
                <Chip
                  label={paymentPillLabel(letter, formatCurrency)}
                  size="small"
                  sx={{ height: 18, fontSize: '0.66rem', fontWeight: 600, px: 0.5, ...paymentStatusSx(theme, letter.paymentStatus) }}
                />
              </>
            )}
          </Stack>

          {/* Payments expand — fines only */}
          {letter.amount != null && (
            <Box sx={{ mt: 0.5 }}>
              <Button
                size="small"
                onClick={togglePayments}
                startIcon={expanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none', color: 'text.secondary', fontSize: '0.7rem',
                  px: 0.5, py: 0, minHeight: 0, minWidth: 0,
                }}
              >
                Payments{payments ? ` (${payments.length})` : ''}
              </Button>
              <Collapse in={expanded}>
                <Box sx={{ mt: 0.5, pl: 1 }}>
                  {loadingPayments ? (
                    <Skeleton variant="rounded" height={30} />
                  ) : (payments?.length ?? 0) === 0 ? (
                    <Typography variant="caption" color="text.disabled">No payments yet.</Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      {(payments || []).map(p => (
                        <Stack
                          key={p._id}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                        >
                          <Typography variant="caption" sx={{ minWidth: 64 }}>{formatDate(p.collectedAt)}</Typography>
                          <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.success.main, minWidth: 70 }}>
                            {formatCurrency(p.amount || 0)}
                          </Typography>
                          <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                            {p.method.replace('_', ' ')}
                          </Typography>
                          {p.referenceNumber && (
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
                              {p.referenceNumber}
                            </Typography>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="actions"
          sx={{ mt: -0.25 }}
        >
          <MoreVert sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              minWidth: 200,
              mt: 0.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 6px 24px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
              '& .MuiList-root': { py: 0.5 },
              '& .MuiMenuItem-root': {
                px: 1.5, py: 0.75, gap: 1.25,
                fontSize: '0.825rem',
                color: 'text.primary',
                borderRadius: 1,
                mx: 0.5,
                '& .MuiListItemIcon-root': { minWidth: 0, color: 'text.secondary' },
                '& .MuiSvgIcon-root': { fontSize: 16 },
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) },
              },
            },
          },
        }}
      >
        <MenuItem onClick={async () => { if (downloading) return; await onAction('download', letter); setMenuAnchor(null); }}>
          <ListItemIcon><BusyIcon busy={downloading} size={14}><Download /></BusyIcon></ListItemIcon>
          <ListItemText primary={downloading ? 'Preparing…' : 'Download PDF'} />
        </MenuItem>
        {canPay && (
          <MenuItem onClick={() => { onAction('pay', letter); setMenuAnchor(null); }}>
            <ListItemIcon><Payments /></ListItemIcon>
            <ListItemText primary="Record payment" />
          </MenuItem>
        )}
        {canWaive && (
          <MenuItem onClick={() => { onAction('waive', letter); setMenuAnchor(null); }}>
            <ListItemIcon><MoneyOff /></ListItemIcon>
            <ListItemText primary="Waive fine" />
          </MenuItem>
        )}
        {canResolve && (
          <MenuItem onClick={() => { onAction('resolve', letter); setMenuAnchor(null); }}>
            <ListItemIcon><CheckCircle /></ListItemIcon>
            <ListItemText primary="Mark resolved" />
          </MenuItem>
        )}
        {canCancel && (
          <>
            <Divider sx={{ my: 0.5, mx: 1 }} />
            <MenuItem onClick={() => { onAction('cancel', letter); setMenuAnchor(null); }}>
              <ListItemIcon><Cancel /></ListItemIcon>
              <ListItemText primary="Cancel letter" />
            </MenuItem>
            <MenuItem
              onClick={() => { onAction('delete', letter); setMenuAnchor(null); }}
              sx={{
                '&&': {
                  color: theme.palette.error.main,
                  '& .MuiListItemIcon-root': { color: theme.palette.error.main },
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
                },
              }}
            >
              <ListItemIcon><Delete /></ListItemIcon>
              <ListItemText primary="Delete" />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

// ─── Compact KPI strip ───────────────────────────────────────────────────────
// A single thin Paper with values separated by inline dividers. Way slimmer
// than 4 individual bordered cards in the narrow drawer.
const KpiInline: React.FC<{
  items: { label: string; value: React.ReactNode; tone?: string }[];
}> = ({ items }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      {items.map((it, i) => (
        <React.Fragment key={it.label}>
          <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0, px: 0.5 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: it.tone || theme.palette.text.primary, lineHeight: 1.15, fontSize: '0.95rem' }}
            >
              {it.value}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '0.6rem', lineHeight: 1 }}
            >
              {it.label}
            </Typography>
          </Box>
          {i < items.length - 1 && <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />}
        </React.Fragment>
      ))}
    </Paper>
  );
};

const StudentLettersTab: React.FC<Props> = ({ studentId, studentName, onChanged }) => {
  const theme = useTheme();
  const formatCurrency = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const { download: downloadLetter, busyId: downloadingId } = useLetterPdf();
  const { canRead, canWrite } = useModulePermissions();
  const canWriteLetters = canWrite('letters');
  const canWriteFines = canWrite('fines');

  const [summary, setSummary] = useState<StudentLettersSummary | null>(null);
  const [letters, setLetters] = useState<LetterV2[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters.
  const [statusFilter, setStatusFilter] = useState<LetterStatus | 'all'>('all');
  const [paymentFilter, setPaymentFilter] = useState<LetterPaymentStatus | 'all'>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);

  // Modals.
  const [issueOpen, setIssueOpen] = useState(false);
  const [payTarget, setPayTarget] = useState<LetterV2 | null>(null);
  const [waiveTarget, setWaiveTarget] = useState<LetterV2 | null>(null);
  const [cancelTarget, setCancelTarget] = useState<LetterV2 | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<LetterV2 | null>(null);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (statusFilter !== 'all') n++;
    if (paymentFilter !== 'all') n++;
    if (from) n++;
    if (to) n++;
    return n;
  }, [statusFilter, paymentFilter, from, to]);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        letterService.listStudentLetters(studentId, {
          status: statusFilter,
          paymentStatus: paymentFilter,
          from: from || undefined,
          to: to || undefined,
          limit: 100,
        }),
        letterService.getStudentLettersSummary(studentId),
      ]);
      setLetters(list.items);
      setSummary(sum);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load letters', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [studentId, statusFilter, paymentFilter, from, to, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const reload = useCallback(() => { load(); onChanged?.(); }, [load, onChanged]);

  const clearFilters = () => {
    setStatusFilter('all');
    setPaymentFilter('all');
    setFrom('');
    setTo('');
  };

  const onAction = useCallback(async (
    kind: 'download' | 'pay' | 'waive' | 'resolve' | 'cancel' | 'delete',
    letter: LetterV2
  ) => {
    try {
      switch (kind) {
        case 'download': await downloadLetter(letter._id); break;
        case 'pay':      setPayTarget(letter); break;
        case 'waive':    setWaiveTarget(letter); break;
        case 'resolve':
          await letterService.resolveLetter(letter._id);
          enqueueSnackbar('Letter resolved', { variant: 'success' });
          reload();
          break;
        case 'cancel':
          setCancelTarget(letter);
          setCancelReason('');
          break;
        case 'delete':
          setDeleteTarget(letter);
          break;
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Action failed', { variant: 'error' });
    }
  }, [enqueueSnackbar, reload, downloadLetter]);

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await letterService.cancelLetter(cancelTarget._id, cancelReason.trim());
      enqueueSnackbar('Letter cancelled', { variant: 'success' });
      setCancelTarget(null);
      setCancelReason('');
      reload();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to cancel', { variant: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await letterService.deleteLetter(deleteTarget._id);
      enqueueSnackbar('Letter deleted', { variant: 'success' });
      setDeleteTarget(null);
      reload();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to delete', { variant: 'error' });
    }
  };

  if (!canRead('letters')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled' }}>
        <MailIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2">You don't have permission to view letters.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header — minimal */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Commendations, notices, warnings, fines, terminations
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Filters">
            <IconButton size="small" onClick={(e) => setFilterAnchor(e.currentTarget)}>
              <Badge color="primary" badgeContent={activeFilterCount} invisible={activeFilterCount === 0}>
                <FilterList sx={{ fontSize: 18 }} />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load}><Refresh sx={{ fontSize: 18 }} /></IconButton>
          </Tooltip>
          {canWriteLetters && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Add sx={{ fontSize: 16 }} />}
              onClick={() => setIssueOpen(true)}
              sx={{ borderRadius: 2, textTransform: 'none', py: 0.5, ml: 0.5 }}
            >
              Issue
            </Button>
          )}
        </Stack>
      </Stack>

      {/* KPI strip — single thin Paper with inline dividers */}
      <Box sx={{ mb: 1.5 }}>
        <KpiInline items={[
          { label: 'Active', value: summary?.active ?? 0, tone: theme.palette.info.main },
          { label: 'Outstanding', value: formatCurrency(summary?.outstandingAmount || 0), tone: theme.palette.error.main },
          { label: 'Resolved', value: summary?.resolved ?? 0, tone: theme.palette.success.main },
          { label: 'Cancelled', value: summary?.cancelled ?? 0, tone: theme.palette.text.disabled },
        ]} />
      </Box>

      {/* Active filter pills (only when any are set) */}
      {activeFilterCount > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1.5 }} useFlexGap>
          {statusFilter !== 'all' && (
            <Chip label={`Status: ${statusFilter}`} size="small" onDelete={() => setStatusFilter('all')} sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
          {paymentFilter !== 'all' && (
            <Chip label={`Payment: ${paymentFilter}`} size="small" onDelete={() => setPaymentFilter('all')} sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
          {from && <Chip label={`From ${from}`} size="small" onDelete={() => setFrom('')} sx={{ height: 22, fontSize: '0.7rem' }} />}
          {to && <Chip label={`To ${to}`} size="small" onDelete={() => setTo('')} sx={{ height: 22, fontSize: '0.7rem' }} />}
          <Button size="small" startIcon={<ClearAll sx={{ fontSize: 14 }} />} onClick={clearFilters}
            sx={{ textTransform: 'none', minHeight: 22, fontSize: '0.7rem', py: 0, px: 0.5 }}>
            Clear
          </Button>
        </Stack>
      )}

      {/* Filter popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, mt: 0.5, width: 280, borderRadius: 2 } }}
      >
        <Stack spacing={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value as any)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Payment</InputLabel>
            <Select
              value={paymentFilter}
              onChange={(e: SelectChangeEvent) => setPaymentFilter(e.target.value as any)}
              label="Payment"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="waived">Waived</MenuItem>
            </Select>
          </FormControl>
          <DateRangePicker
            fullWidth
            label="Date range"
            value={{ from: from || null, to: to || null }}
            onChange={(r) => { setFrom(r.from || ''); setTo(r.to || ''); }}
          />
          <Stack direction="row" justifyContent="space-between">
            <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>Clear all</Button>
            <Button size="small" variant="contained" onClick={() => setFilterAnchor(null)} sx={{ textTransform: 'none' }}>Done</Button>
          </Stack>
        </Stack>
      </Popover>

      {/* Timeline — flat list inside a single Paper */}
      {loading ? (
        <Stack spacing={1}>
          {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={56} />)}
        </Stack>
      ) : letters.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 2,
          border: `1px dashed ${theme.palette.divider}`,
          textAlign: 'center',
        }}>
          <MailIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">No letters yet</Typography>
          {canWriteLetters && (
            <Button size="small" startIcon={<Add sx={{ fontSize: 14 }} />} onClick={() => setIssueOpen(true)}
              sx={{ textTransform: 'none', mt: 1 }}>
              Issue first letter
            </Button>
          )}
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
          {letters.map(l => (
            <LetterTimelineItem
              key={l._id}
              letter={l}
              onAction={onAction}
              canWriteLetters={canWriteLetters}
              canWriteFines={canWriteFines}
              downloading={downloadingId === l._id}
            />
          ))}
        </Paper>
      )}

      {/* Dialogs */}
      <IssueLetterDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        studentId={studentId}
        studentName={studentName}
        onIssued={reload}
      />
      <RecordPaymentDialog
        open={Boolean(payTarget)}
        onClose={() => setPayTarget(null)}
        letter={payTarget}
        onRecorded={reload}
      />
      <WaiveFineDialog
        open={Boolean(waiveTarget)}
        onClose={() => setWaiveTarget(null)}
        letter={waiveTarget}
        onWaived={reload}
      />
      <ConfirmationDialog
        open={Boolean(cancelTarget)}
        onClose={() => { setCancelTarget(null); setCancelReason(''); }}
        onConfirm={handleCancelConfirm}
        title="Cancel letter?"
        message="Cancelling preserves the audit trail. The letter stays visible (dimmed) and is excluded from active counts and outstanding totals."
        confirmText="Cancel Letter"
        cancelText="Keep"
        confirmColor="warning"
        confirmDisabled={!cancelReason.trim()}
      >
        <TextField
          fullWidth
          size="small"
          label="Reason"
          placeholder="Why is this letter being cancelled?"
          multiline
          minRows={2}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          autoFocus
          sx={{ mt: 1 }}
        />
      </ConfirmationDialog>
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete letter?"
        message="This removes the letter from the student's record. This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default StudentLettersTab;
